import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Plus, AlertTriangle, Calendar, FileText, Heart, Pill, Activity, ChevronDown, ChevronUp, X, Check, Clock, User } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =====================================================
// COMPONENTE MEDICO: Prescrivi Protocollo
// =====================================================
export const PrescribeProtocolForm = ({ patientId, patientName, onClose, onSuccess }) => {
  const [pathologies, setPathologies] = useState([]);
  const [selectedPathology, setSelectedPathology] = useState('');
  const [priority, setPriority] = useState('media');
  const [reason, setReason] = useState('');
  const [customFrequency, setCustomFrequency] = useState('');
  const [customNotes, setCustomNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPathologies, setLoadingPathologies] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadPathologies();
  }, []);

  const loadPathologies = async () => {
    try {
      const res = await axios.get(`${API}/prevention/pathologies-list`);
      setPathologies(res.data.pathologies || []);
    } catch (error) {
      console.error('Error loading pathologies:', error);
      // Fallback lista base
      setPathologies([
        { id: 'glaucoma', name: 'Glaucoma', category: 'Oculare' },
        { id: 'maculopatia_amd', name: 'Maculopatia AMD', category: 'Retinica' },
        { id: 'retinopatia_diabetica', name: 'Retinopatia Diabetica', category: 'Retinica' },
        { id: 'cataratta', name: 'Cataratta', category: 'Cristallino' },
        { id: 'dry_eye', name: 'Occhio Secco', category: 'Superficie' },
        { id: 'cheratocono', name: 'Cheratocono', category: 'Cornea' },
        { id: 'miopia_patologica', name: 'Miopia Patologica', category: 'Refrattiva' }
      ]);
    } finally {
      setLoadingPathologies(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPathology || !reason) {
      toast.error('Seleziona una patologia e inserisci la motivazione');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/prevention/prescribe`, {
        patient_id: patientId,
        pathology_id: selectedPathology,
        priority,
        reason,
        custom_frequency: customFrequency || null,
        custom_notes: customNotes || null
      }, { headers });

      toast.success(res.data.message || 'Protocollo prescritto con successo!');
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('Error prescribing protocol:', error);
      toast.error(error.response?.data?.detail || 'Errore nella prescrizione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#2A9FCB] p-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-white" />
            <div>
              <h2 className="text-lg font-bold text-white">Prescrivi Protocollo</h2>
              <p className="text-white/80 text-sm">Paziente: {patientName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Patologia */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patologia da monitorare *
            </label>
            {loadingPathologies ? (
              <div className="animate-pulse bg-gray-200 h-10 rounded-lg"></div>
            ) : (
              <select
                value={selectedPathology}
                onChange={(e) => setSelectedPathology(e.target.value)}
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]/30 focus:border-[#2A9FCB]"
                required
              >
                <option value="">Seleziona patologia...</option>
                {pathologies.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.category})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Priorità */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Priorità
            </label>
            <div className="flex gap-2">
              {[
                { value: 'alta', label: 'Alta', color: 'bg-red-100 text-red-700 border-red-300' },
                { value: 'media', label: 'Media', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
                { value: 'bassa', label: 'Bassa', color: 'bg-green-100 text-green-700 border-green-300' }
              ].map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPriority(p.value)}
                  className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all ${
                    priority === p.value ? p.color + ' border-current' : 'bg-gray-50 text-gray-500 border-gray-200'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Motivazione */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivazione *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]/30 focus:border-[#2A9FCB]"
              rows={3}
              placeholder="es: Familiarità per glaucoma, IOP borderline..."
              required
            />
          </div>

          {/* Frequenza personalizzata */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Frequenza visite (opzionale)
            </label>
            <select
              value={customFrequency}
              onChange={(e) => setCustomFrequency(e.target.value)}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]/30 focus:border-[#2A9FCB]"
            >
              <option value="">Usa frequenza standard</option>
              <option value="Ogni 3 mesi">Ogni 3 mesi</option>
              <option value="Ogni 6 mesi">Ogni 6 mesi</option>
              <option value="Annuale">Annuale</option>
              <option value="Ogni 2 anni">Ogni 2 anni</option>
            </select>
          </div>

          {/* Note aggiuntive */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note per il paziente (opzionale)
            </label>
            <textarea
              value={customNotes}
              onChange={(e) => setCustomNotes(e.target.value)}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]/30 focus:border-[#2A9FCB]"
              rows={2}
              placeholder="Consigli specifici, avvertenze..."
            />
          </div>

          {/* Bottoni */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 px-4 bg-[#2A9FCB] text-white rounded-xl font-medium hover:bg-[#238bb3] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Salvataggio...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Prescrivi Protocollo
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// =====================================================
// COMPONENTE PAZIENTE: Visualizza Protocolli
// =====================================================
export const PatientProtocolsView = ({ patientId }) => {
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProtocol, setExpandedProtocol] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (patientId) {
      loadProtocols();
    }
  }, [patientId]);

  const loadProtocols = async () => {
    try {
      const res = await axios.get(`${API}/prevention/patient/${patientId}/protocols`, { headers });
      setProtocols(res.data.protocols || []);
    } catch (error) {
      console.error('Error loading protocols:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      alta: 'bg-red-100 text-red-700 border-red-200',
      media: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      bassa: 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[priority] || colors.media;
  };

  const getPriorityIcon = (priority) => {
    if (priority === 'alta') return <AlertTriangle className="w-4 h-4" />;
    return <Shield className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2A9FCB] to-[#1C729E] p-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white font-['Playfair_Display']">
              I Tuoi Protocolli di Prevenzione
            </h2>
            <p className="text-white/80 text-sm">
              {protocols.length > 0 
                ? `${protocols.length} protocollo${protocols.length > 1 ? 'i' : ''} attivo${protocols.length > 1 ? 'i' : ''}`
                : 'Nessun protocollo prescritto'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {protocols.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Shield className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">Nessun protocollo prescritto</p>
            <p className="text-sm mt-2">
              Il medico ti prescriverà un protocollo di prevenzione personalizzato dopo la visita.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {protocols.map((protocol) => (
              <div 
                key={protocol.id}
                className="border rounded-xl overflow-hidden transition-all hover:shadow-md"
              >
                {/* Protocol Header */}
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedProtocol(expandedProtocol === protocol.id ? null : protocol.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${getPriorityColor(protocol.priority)}`}>
                        {getPriorityIcon(protocol.priority)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{protocol.pathology_name}</h3>
                        <p className="text-sm text-gray-500">{protocol.reason}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-1 rounded-full border ${getPriorityColor(protocol.priority)}`}>
                        Priorità {protocol.priority}
                      </span>
                      {expandedProtocol === protocol.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Protocol Details (Expanded) */}
                {expandedProtocol === protocol.id && (
                  <div className="border-t bg-gray-50 p-4 space-y-4">
                    {/* Frequenza visite */}
                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-[#2A9FCB] mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-700">Frequenza Controlli</p>
                        <p className="text-sm text-gray-600">{protocol.visit_frequency}</p>
                      </div>
                    </div>

                    {/* Esami consigliati */}
                    {protocol.recommended_exams?.length > 0 && (
                      <div className="flex items-start gap-3">
                        <FileText className="w-5 h-5 text-[#2A9FCB] mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-700">Esami Consigliati</p>
                          <ul className="text-sm text-gray-600 mt-1 space-y-1">
                            {protocol.recommended_exams.map((exam, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-[#2A9FCB] rounded-full"></span>
                                {exam}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* Stile di vita */}
                    {protocol.lifestyle_tips?.length > 0 && (
                      <div className="flex items-start gap-3">
                        <Heart className="w-5 h-5 text-green-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-700">Consigli Stile di Vita</p>
                          <ul className="text-sm text-gray-600 mt-1 space-y-1">
                            {protocol.lifestyle_tips.map((tip, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                                {typeof tip === 'object' ? tip.action : tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* Integratori */}
                    {protocol.supplements?.length > 0 && (
                      <div className="flex items-start gap-3">
                        <Pill className="w-5 h-5 text-purple-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-700">Integratori Consigliati</p>
                          <ul className="text-sm text-gray-600 mt-1 space-y-1">
                            {protocol.supplements.map((supp, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                                {typeof supp === 'object' ? supp.name : supp}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* Note medico */}
                    {protocol.doctor_notes && (
                      <div className="flex items-start gap-3">
                        <User className="w-5 h-5 text-gray-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-700">Note del Medico</p>
                          <p className="text-sm text-gray-600 italic">"{protocol.doctor_notes}"</p>
                        </div>
                      </div>
                    )}

                    {/* Data prescrizione */}
                    <div className="flex items-center gap-2 text-xs text-gray-400 pt-2 border-t">
                      <Clock className="w-4 h-4" />
                      Prescritto il {new Date(protocol.prescribed_at).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientProtocolsView;
